import { create } from 'zustand';
import {
  listContracts,
  searchContracts,
  getContract,
  getContractTypes,
  type ContractSummary,
  type ContractDetail,
  type ContractSearchHit,
} from '../services/contractsApi';

export type ContractDisplayFormat = 'markdown' | 'html';

interface ContractsState {
  // Search/list
  query: string;
  statusFilter: string;
  typeFilter: string;
  yearFrom: string;
  yearTo: string;
  results: ContractSearchHit[];
  browse: ContractSummary[];     // used when query is empty
  loading: boolean;
  hasMore: boolean;
  offset: number;

  // Types for filter dropdown
  types: string[];

  // Active contract
  activeId: string | null;
  activeContract: ContractDetail | null;
  loadingDetail: boolean;

  // Display settings
  displayFormat: ContractDisplayFormat;

  // Actions
  setQuery: (q: string) => void;
  setStatusFilter: (s: string) => void;
  setTypeFilter: (t: string) => void;
  setYearFrom: (y: string) => void;
  setYearTo: (y: string) => void;
  setDisplayFormat: (f: ContractDisplayFormat) => void;
  search: () => Promise<void>;
  loadMore: () => Promise<void>;
  loadTypes: () => Promise<void>;
  openContract: (id: string) => Promise<void>;
  closeContract: () => void;
}

const PAGE = 30;

function yearToDateFrom(y: string) { return y ? `${y}-01-01` : undefined; }
function yearToDateTo(y: string) { return y ? `${y}-12-31` : undefined; }

export const useContractsStore = create<ContractsState>((set, get) => ({
  query: '',
  statusFilter: '',
  typeFilter: '',
  yearFrom: '',
  yearTo: '',
  results: [],
  browse: [],
  loading: false,
  hasMore: false,
  offset: 0,
  types: [],
  activeId: null,
  activeContract: null,
  loadingDetail: false,
  displayFormat: (localStorage.getItem('contractsFormat') as ContractDisplayFormat) || 'markdown',

  setQuery: (q) => set({ query: q }),
  setStatusFilter: (s) => set({ statusFilter: s }),
  setTypeFilter: (t) => set({ typeFilter: t }),
  setYearFrom: (y) => set({ yearFrom: y }),
  setYearTo: (y) => set({ yearTo: y }),
  setDisplayFormat: (f) => {
    localStorage.setItem('contractsFormat', f);
    set({ displayFormat: f });
  },

  search: async () => {
    const { query, statusFilter, typeFilter, yearFrom, yearTo } = get();
    const date_from = yearToDateFrom(yearFrom);
    const date_to = yearToDateTo(yearTo);
    set({ loading: true, offset: 0 });
    try {
      if (query.trim().length >= 2) {
        const res = await searchContracts(query, {
          status: statusFilter || undefined,
          type_doc: typeFilter || undefined,
          limit: PAGE,
          offset: 0,
          date_from,
          date_to,
        });
        set({ results: res, browse: [], hasMore: res.length === PAGE, offset: PAGE, loading: false });
      } else {
        const res = await listContracts({
          status: statusFilter || undefined,
          type_doc: typeFilter || undefined,
          limit: PAGE,
          offset: 0,
          date_from,
          date_to,
        });
        set({ browse: res, results: [], hasMore: res.length === PAGE, offset: PAGE, loading: false });
      }
    } catch {
      set({ loading: false });
    }
  },

  loadMore: async () => {
    const { query, statusFilter, typeFilter, yearFrom, yearTo, offset, results, browse, loading } = get();
    if (loading) return;
    const date_from = yearToDateFrom(yearFrom);
    const date_to = yearToDateTo(yearTo);
    set({ loading: true });
    try {
      if (query.trim().length >= 2) {
        const more = await searchContracts(query, {
          status: statusFilter || undefined,
          type_doc: typeFilter || undefined,
          limit: PAGE,
          offset,
          date_from,
          date_to,
        });
        set({ results: [...results, ...more], hasMore: more.length === PAGE, offset: offset + PAGE, loading: false });
      } else {
        const more = await listContracts({
          status: statusFilter || undefined,
          type_doc: typeFilter || undefined,
          limit: PAGE,
          offset,
          date_from,
          date_to,
        });
        set({ browse: [...browse, ...more], hasMore: more.length === PAGE, offset: offset + PAGE, loading: false });
      }
    } catch {
      set({ loading: false });
    }
  },

  loadTypes: async () => {
    try {
      const types = await getContractTypes();
      set({ types });
    } catch { /* ignore */ }
  },

  openContract: async (id) => {
    if (get().activeId === id && get().activeContract) return;
    set({ activeId: id, loadingDetail: true, activeContract: null });
    try {
      const c = await getContract(id);
      set({ activeContract: c, loadingDetail: false });
    } catch {
      set({ loadingDetail: false });
    }
  },

  closeContract: () => set({ activeId: null, activeContract: null }),
}));
